import React, { useState, useEffect, useRef } from "react";
import MapGL, { Marker, Source, Layer, ViewState } from "react-map-gl";

import { useHazardMap } from "./../riskAssessment/HazardMapContext";
import RenderHazardMap from "./../riskAssessment/RenderHazardMap";

import { defaultRiskAssessmentMapStyle } from "../map/Constants";
import { defaultSourceLayer, MapSourceData } from "./../riskAssessment/defaultSourceLayer";
import { ScaleBar } from "../map/ScaleBar";
import { defaultRomeLatitude, defaultRomeLongitude } from "../map/Constants";

import Pin from "./pin";

import AgriPartitasRenderer from "./AgriPartitasRenderer";

import "mapbox-gl/dist/mapbox-gl.css";
import { MapStylesMenu } from "./MapStylesMenu";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

type AgriculturePolicyDetailsMapProps = {
  latitude: number;
  longitude: number;
  policyNumber: string;
  currentlyVisiblePartitas: any;
};

const AgriculturePolicyDetailsMap: React.FC<AgriculturePolicyDetailsMapProps> = ({
  longitude,
  latitude,
  policyNumber,
  ...props
}) => {
  const [currentLatitude, setCurrentLatitude] = useState(latitude);
  const [currentLongitude, setCurrentLongitude] = useState(longitude);
  const [showWms, setShowWms] = useState(false);
  const [showWmsTransparent, setShowWmsTransparent] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(17);

  // RenderHazardMap states
  const { selectedHazard, onHazardChange } = useHazardMap();
  const [sourceLayerData, setSourceLayerData] = useState<MapSourceData>(defaultSourceLayer); // when hazard config has source layer
  const [sourceLayerDataSecondary, setSourceLayerDataSecondary] = useState<MapSourceData>(defaultSourceLayer); // when hazard config has 2nd source layer
  const [mapStyle, setMapStyle] = useState(defaultRiskAssessmentMapStyle); // when hazard config has mayStyle

  const [viewState, setViewState] = useState({
    latitude: defaultRomeLatitude,
    longitude: defaultRomeLongitude,
    zoom: 17,
  });

  useEffect(() => {
    setViewState((old) => ({
      ...old,
      latitude: currentLatitude,
      longitude: currentLongitude,
      zoom: zoomLevel,
    }));
  }, [currentLatitude, currentLongitude, zoomLevel]);

  useEffect(() => {
    if (selectedHazard) {
      setShowWms(false);
      setShowWmsTransparent(false);
    }
  }, [selectedHazard]);

  const onMove = (viewState: ViewState) => {
    setViewState(viewState);
  };

  const [isBasicMapStyle, setIsBasicMapStyle] = useState(true);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const styleRevRef = useRef(0);

  const onStyleChange = (newStyle: string) => {
    if (mapStyle !== newStyle) {
      console.log("🔁 Changing map style to", newStyle);
      styleRevRef.current += 1; // force Map remount
      setStyleLoaded(false); // gate Sources until onLoad
      setMapStyle(newStyle);
    } else {
      console.log("ℹ️ Map style already set to", newStyle);
    }
  };

  const tilePath =
    process.env.REACT_APP_BASE_URL +
    "/wms?bbox={bbox-epsg-3857}" +
    (showWmsTransparent ? "&transparent=true" : "");

  const setMapStyleAndClearHazardLayer = (mapStyle: string) => {
    onHazardChange(null);

    const value = mapStyle;
    // if e.target.value starts with "wms+" then remove it
    if (value.includes("wms+")) {
      mapStyle = value.replace("wms+", "");
      setShowWms(true);
      setShowWmsTransparent(false);
    } else if (value.includes("wmst+")) {
      mapStyle = value.replace("wmst+", "");
      setShowWms(true);
      setShowWmsTransparent(true);
    } else {
      setShowWms(false);
      setShowWmsTransparent(false);
    }

    // setMapStyle(mapStyle);
    onStyleChange(mapStyle);

    setSourceLayerData(defaultSourceLayer);
    setSourceLayerDataSecondary(defaultSourceLayer);
    setIsBasicMapStyle(true);
  };

  return (
    <MapGL
      key={`map-${styleRevRef.current}`} // force full remount on style change
      {...viewState}
      mapboxAccessToken={TOKEN}
      style={{ width: "100%", height: "700px" }}
      mapStyle={mapStyle}
      onLoad={() => {
        setStyleLoaded(true);
        console.log("✅ Map loaded; style ready. rev:", styleRevRef.current, "style:", mapStyle);
      }}
      onMove={(evt) => onMove(evt.viewState)}
    >
      {showWms && (
        <>
          <Source id="wms-source" type="raster" tiles={[tilePath]} tileSize={256} />

          <Layer
            id="wms-layer"
            type="raster"
            source="wms-source"
            paint={{ "raster-opacity": showWmsTransparent ? 1 : 0.5 }}
          />
        </>
      )}
      <RenderHazardMap
        setMapStyle={onStyleChange}
        selectedHazard={selectedHazard}
        sourceLayerData={sourceLayerData}
        setSourceLayerData={setSourceLayerData}
        sourceLayerDataSecondary={sourceLayerDataSecondary}
        setSourceLayerDataSecondary={setSourceLayerDataSecondary}
        styleLoaded={styleLoaded}
        isBasicMapStyle={isBasicMapStyle}
        setBasicMapStyle={setIsBasicMapStyle}
      />

      {currentLongitude && currentLatitude && (
        <Marker longitude={currentLongitude} latitude={currentLatitude}>
          <Pin size={20} />
        </Marker>
      )}

      {styleLoaded && (
        <AgriPartitasRenderer
          policyNumber={policyNumber}
          setCurrentLongitude={setCurrentLongitude}
          setCurrentLatitude={setCurrentLatitude}
          setZoomLevel={setZoomLevel}
          currentlyVisiblePartitas={props.currentlyVisiblePartitas}
        />
      )}

      <MapStylesMenu setMapStyle={setMapStyleAndClearHazardLayer} addWms={true}></MapStylesMenu>

      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};

export default AgriculturePolicyDetailsMap;
